import type { PerpMarket } from "@/hooks/airdrop-farming/types";
import { MarketType } from "@cede/trading-data";
import { assignMarketCategories } from "@/utils/trading";

// ── Raw Paradex API types ─────────────────────────────────────────────────────

export interface ParadexRawMarket {
  symbol: string; // "BTC-USD-PERP"
  base_currency: string; // "BTC"
  quote_currency: string; // "USD"
  settlement_currency: string; // "USDC"
  asset_kind: string; // "PERP"
  market_kind: string; // "cross"
  order_size_increment: string; // "0.001" — used to derive szDecimals
  price_tick_size: string;
  min_notional: string; // "10"
  max_order_size: string;
  max_funding_rate: string;
  delta1_cross_margin_params?: {
    imf_base: string; // e.g. "0.05" → max leverage = 1 / 0.05 = 20x
    imf_factor: string;
    mmf_factor: string;
    imf_shift: string;
  };
  tags?: string[];
}

export interface ParadexRawMarketSummary {
  symbol: string;
  mark_price: string;
  bid: string;
  ask: string;
  volume_24h: string;
  open_interest: string;
  funding_rate: string;
  /** Decimal rate — e.g. 0.0894 means +8.94%. */
  price_change_rate_24h: string;
}

export interface ParadexMarketsResponse {
  results: ParadexRawMarket[];
}

export interface ParadexMarketsSummaryResponse {
  results: ParadexRawMarketSummary[];
}

// ── Adapter ───────────────────────────────────────────────────────────────────

/**
 * Normalizes Paradex REST API responses to the generic PerpMarket format.
 *
 * Key mapping notes:
 * - No `status` field in the API — all markets returned are active by design
 * - `order_size_increment` replaces the non-existent `step_size`
 * - `maxLeverage` is derived from `delta1_cross_margin_params.imf_base`: 1 / imf_base
 * - `price_change_rate_24h` is a decimal rate (0.0894 = +8.94%) → multiply by 100
 * - Paradex is cross-margin only → `onlyIsolated: false`
 */
export function adaptParadexMarkets(markets: ParadexRawMarket[], summaries: ParadexRawMarketSummary[]): PerpMarket[] {
  const summaryBySymbol = new Map<string, ParadexRawMarketSummary>();
  for (const s of summaries) {
    summaryBySymbol.set(s.symbol, s);
  }

  return markets
    .filter((m) => m.asset_kind === "PERP")
    .map((market): PerpMarket => {
      const summary = summaryBySymbol.get(market.symbol);

      const markPrice = summary?.mark_price;
      const currentPrice = markPrice ? parseFloat(markPrice) : 0;

      // Decimal rate → percentage
      const rawRate = summary ? parseFloat(summary.price_change_rate_24h) : NaN;
      const priceChange24h = isNaN(rawRate) ? 0 : rawRate * 100;

      const prevDayPrice =
        summary && currentPrice > 0 && !isNaN(rawRate) && rawRate !== -1
          ? (currentPrice / (1 + rawRate)).toString()
          : undefined;

      const bid = summary ? parseFloat(summary.bid) : 0;
      const ask = summary ? parseFloat(summary.ask) : 0;
      const midPrice = bid > 0 && ask > 0 ? ((bid + ask) / 2).toString() : markPrice;

      // szDecimals from order_size_increment: "0.001" → 3, "1" → 0
      const decimalPart = market.order_size_increment.split(".")[1];
      const szDecimals = decimalPart ? decimalPart.length : 0;

      // maxLeverage from imf_base: 1 / 0.05 = 20x
      const imfBase = market.delta1_cross_margin_params?.imf_base;
      const maxLeverage = imfBase ? Math.round(1 / parseFloat(imfBase)) : undefined;

      return {
        type: MarketType.PERP,
        symbol: market.symbol, // "BTC-USD-PERP"
        pair: `${market.base_currency}/${market.quote_currency}:${market.settlement_currency}`, // "BTC/USD:USDC"
        baseAsset: market.base_currency, // "BTC"
        quoteAsset: market.settlement_currency, // "USDC"
        markPrice,
        midPrice,
        volume24h: summary?.volume_24h,
        priceChange24h,
        prevDayPrice,
        funding: summary?.funding_rate,
        openInterest: summary?.open_interest,
        maxLeverage,
        szDecimals,
        onlyIsolated: false,
        isDelisted: false,
        isGrowthMode: false,
        searchString: `${market.base_currency.toLowerCase()}perp`,
        categories: assignMarketCategories(market.base_currency, MarketType.PERP),
      };
    });
}
